/**
 * Mouse drag-to-scroll for native horizontal overflow scrollers.
 *
 * Touch and trackpad scrolling stay native; this only adds click-and-drag
 * for mouse users, with the same feel as a touch flick: the row follows the
 * pointer 1:1 and keeps gliding after release, slowing down with friction.
 * Pair it with `scroll-snap-type: none` for fine pointers, otherwise the
 * browser snaps to the nearest item the moment the drag ends.
 *
 * The element gets `is-draggable` while its content overflows (so CSS can
 * show a grab cursor) and `is-dragging` during a drag. A drag never
 * registers as a click on the element's links or buttons.
 *
 * @param {HTMLElement} el - The overflow-x scroller.
 * @param {object} [options]
 * @param {number} [options.threshold=3] - Pixels of movement before it counts as a drag.
 * @param {number} [options.friction=0.95] - Velocity kept per 16ms frame after release (0 = stop dead).
 * @returns {() => void} Tear-down function.
 */
export function enableDragScroll(el, { threshold = 3, friction = 0.95 } = {}) {
  if (!el) return () => {};

  let isDown = false;
  let moved = false;
  let startX = 0;
  let startScroll = 0;
  let lastX = 0;
  let lastTime = 0;
  let velocity = 0; // px per ms, positive = content moving left
  let glideFrame = null;

  const stopGlide = () => {
    if (glideFrame) cancelAnimationFrame(glideFrame);
    glideFrame = null;
  };

  const glide = () => {
    let previous = performance.now();
    const step = (now) => {
      const dt = Math.min(now - previous, 50);
      previous = now;
      el.scrollLeft += velocity * dt;
      velocity *= friction ** (dt / 16);
      const atEdge = el.scrollLeft <= 0 || el.scrollLeft >= el.scrollWidth - el.clientWidth;
      if (Math.abs(velocity) < 0.02 || atEdge) {
        glideFrame = null;
        return;
      }
      glideFrame = requestAnimationFrame(step);
    };
    glideFrame = requestAnimationFrame(step);
  };

  const syncDraggable = () => {
    el.classList.toggle('is-draggable', el.scrollWidth > el.clientWidth + 1);
  };

  const onMouseDown = (e) => {
    if (e.button !== 0 || el.scrollWidth <= el.clientWidth) return;
    stopGlide();
    isDown = true;
    moved = false;
    startX = e.pageX;
    startScroll = el.scrollLeft;
    lastX = e.pageX;
    lastTime = performance.now();
    velocity = 0;
    el.classList.add('is-dragging');
    e.preventDefault();
  };

  const onMouseMove = (e) => {
    if (!isDown) return;
    const dx = e.pageX - startX;
    if (Math.abs(dx) > threshold) moved = true;
    el.scrollLeft = startScroll - dx;

    // Smoothed release velocity from the last pointer samples.
    const now = performance.now();
    const dt = now - lastTime;
    if (dt > 0) {
      const sample = (lastX - e.pageX) / dt;
      velocity = velocity * 0.6 + sample * 0.4;
      lastX = e.pageX;
      lastTime = now;
    }
  };

  const end = () => {
    if (!isDown) return;
    isDown = false;
    el.classList.remove('is-dragging');
    // A pause before release means no flick.
    if (performance.now() - lastTime > 80) velocity = 0;
    if (moved && Math.abs(velocity) > 0.05) glide();
  };

  // Capture phase, so it runs before any delegated click handler up the tree.
  const onClick = (e) => {
    if (!moved) return;
    e.preventDefault();
    e.stopPropagation();
    moved = false;
  };

  el.addEventListener('mousedown', onMouseDown);
  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup', end);
  el.addEventListener('mouseleave', end);
  el.addEventListener('click', onClick, true);

  const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(syncDraggable) : null;
  resizeObserver?.observe(el);
  syncDraggable();

  return () => {
    el.removeEventListener('mousedown', onMouseDown);
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', end);
    el.removeEventListener('mouseleave', end);
    el.removeEventListener('click', onClick, true);
    resizeObserver?.disconnect();
    stopGlide();
    el.classList.remove('is-draggable', 'is-dragging');
  };
}
